<template>
  <div class="container py-4">
    <!-- Page Header -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <div>
        <h1 class="h3 mb-1 visually-hidden">
          Lab Attempts Review and Data Export
        </h1>
      </div>
      <!-- Hiding for now.. not sure if we need this -->
      <button
        v-if="hasCompletedAttempts"
        @click="exportAllAttempts"
        class="btn btn-outline-primary btn-sm d-none"
      >
        Export All Data
      </button>
    </div>

    <!-- Lab attempts list -->
    <div class="row g-3">
      <div
        v-for="attemptNumber in maxAttempts"
        :key="attemptNumber"
        class="col-md-10"
      >
        <div
          class="card h-100 lab-attempt-card"
          :class="`attempt-border-${attemptNumber}`"
        >
          <!-- Attempt Header -->
          <div
            class="card-header d-flex justify-content-between align-items-center"
          >
            <div>
              <h5 class="mb-1">Attempt {{ attemptNumber }}</h5>
              <small class="text-muted">
                {{ getStatusText(attemptNumber) }}
              </small>
            </div>
          </div>

          <!-- Attempt Body -->
          <div class="card-body">
            <!-- Not Started -->
            <div v-if="!getAttemptData(attemptNumber)" class="text-center py-3">
              <p class="text-muted mb-3">Ready to start</p>
              <button
                v-if="canStartThisAttempt(attemptNumber)"
                @click="startNewAttempt"
                class="btn btn-success"
              >
                Start Lab
              </button>
              <p v-else class="text-muted small mb-0">
                Complete previous attempts first
              </p>
            </div>

            <!-- Started -->
            <!-- Started -->
            <div v-else>
              <div class="d-flex justify-content-around">
                <div class="mb-3">
                  <small class="text-muted">Started:</small>
                  <strong class="d-block">
                    {{
                      formatDateTime(getAttemptData(attemptNumber).timestamp)
                    }}
                  </strong>
                </div>

                <div
                  v-if="getAttemptData(attemptNumber).isComplete"
                  class="mb-3"
                >
                  <small class="text-muted">Completed:</small>
                  <strong class="d-block">
                    {{
                      formatDateTime(getAttemptData(attemptNumber).completedAt)
                    }}
                  </strong>
                </div>

                <div class="mb-3">
                  <small class="text-muted">Measurements:</small>
                  <strong class="d-block">
                    {{
                      (getAttemptData(attemptNumber).measurements || []).length
                    }}
                    recorded
                  </strong>
                </div>
              </div>

              <!-- Lab-specific review chart (e.g. colorimeter) when registered -->
              <div
                v-if="
                  reviewChartComponent &&
                  getAttemptData(attemptNumber).isComplete &&
                  getAttemptData(attemptNumber).measurements &&
                  getAttemptData(attemptNumber).measurements.length > 0
                "
                class="mb-3"
              >
                <!-- Chart Toggle Header -->
                <div class="d-flex justify-content-end align-items-center mb-2">
                  <button
                    @click="toggleChart(attemptNumber)"
                    class="btn btn-sm btn-outline-secondary"
                    type="button"
                  >
                    <i
                      :class="
                        isChartCollapsed(attemptNumber)
                          ? 'bi-chevron-down'
                          : 'bi-chevron-up'
                      "
                    ></i>
                    {{
                      isChartCollapsed(attemptNumber)
                        ? "Show Chart"
                        : "Hide Chart"
                    }}
                  </button>
                </div>

                <!-- Collapsible Chart -->
                <div
                  v-show="!isChartCollapsed(attemptNumber)"
                  class="chart-container"
                >
                  <component
                    v-if="reviewChartComponent"
                    :is="reviewChartComponent"
                    :measurements="getAttemptData(attemptNumber).measurements"
                    :show-only-noisy="true"
                    :options="{
                      deviceState: {
                        hidden: true
                      },
                      actionLog: {
                        hidden: true
                      },
                      actionButtons: {
                        hidden: true
                      }
                    }"
                  />
                </div>
              </div>

              <!-- Action Buttons -->
              <div class="d-flex justify-content-end gap-2">
                <button
                  v-if="
                    !getAttemptData(attemptNumber).isComplete ||
                    !locksOnCompletion
                  "
                  @click="resumeAttempt(attemptNumber)"
                  class="btn"
                  :class="
                    getAttemptData(attemptNumber).isComplete
                      ? 'btn-outline-primary'
                      : 'btn-primary'
                  "
                >
                  Continue Lab
                </button>

                <button
                  v-if="getAttemptData(attemptNumber).isComplete"
                  @click="downloadCSV(attemptNumber)"
                  class="btn btn-outline-success"
                >
                  Download CSV
                </button>

                <button
                  v-if="!getAttemptData(attemptNumber).isComplete"
                  @click="confirmRestart(attemptNumber)"
                  class="btn btn-outline-danger btn-sm"
                >
                  Restart
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Simple Restart Confirmation Modal -->
    <div v-if="showRestartModal" class="modal fade show d-block" tabindex="-1">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Restart Lab Attempt?</h5>
          </div>
          <div class="modal-body">
            <p>
              This will delete all data from Attempt
              {{ restartAttemptNumber }} and start fresh.
            </p>
            <p class="text-danger small">
              <strong>This action cannot be undone.</strong>
            </p>
          </div>
          <div class="modal-footer">
            <button @click="cancelRestart" class="btn btn-secondary">
              Cancel
            </button>
            <button @click="executeRestart" class="btn btn-danger">
              Restart
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineAsyncComponent } from "vue";
import { mapGetters, mapActions } from "vuex";
import {
  getReviewComponentLoader,
  exportLabAttemptCSV
} from "@/labRegistry.js";

const reviewChartByLabId = new Map();

function getReviewChartAsyncComponent(labId) {
  const loader = getReviewComponentLoader(labId);
  if (!loader) return null;
  if (!reviewChartByLabId.has(labId)) {
    reviewChartByLabId.set(labId, defineAsyncComponent(loader));
  }
  return reviewChartByLabId.get(labId);
}

export default {
  name: "LabAttemptsView",
  data() {
    return {
      showRestartModal: false,
      restartAttemptNumber: null,
      collapsedCharts: {}
    };
  },
  computed: {
    ...mapGetters([
      "labAttempts",
      "canStartNewAttempt",
      "maxAttempts",
      "locksOnCompletion"
    ]),
    effectiveLabId() {
      return import.meta.env.VITE_LAB_ENV;
    },
    reviewChartComponent() {
      return getReviewChartAsyncComponent(this.effectiveLabId);
    },
    hasCompletedAttempts() {
      return this.labAttempts.some((attempt) => attempt.isComplete);
    }
  },
  methods: {
    ...mapActions(["startNewLabAttempt", "setContext", "removeLabAttempt"]),
    getAttemptData(attemptNumber) {
      const sortedAttempts = [...this.labAttempts].sort(
        (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
      );
      return sortedAttempts[attemptNumber - 1] || null;
    },
    getNextAttemptNumber() {
      return this.labAttempts.length + 1;
    },
    canStartThisAttempt(attemptNumber) {
      if (attemptNumber !== this.getNextAttemptNumber()) {
        return false;
      }
      for (let i = 1; i < attemptNumber; i++) {
        const previousAttempt = this.getAttemptData(i);
        if (previousAttempt && !previousAttempt.isComplete) {
          return false;
        }
      }
      return this.canStartNewAttempt;
    },
    toggleChart(attemptNumber) {
      this.collapsedCharts[attemptNumber] =
        !this.isChartCollapsed(attemptNumber);
    },
    isChartCollapsed(attemptNumber) {
      return this.collapsedCharts[attemptNumber] || false;
    },
    getStatusText(attemptNumber) {
      const attempt = this.getAttemptData(attemptNumber);
      if (!attempt) return "Not Started";
      return attempt.isComplete ? "Completed" : "In Progress";
    },
    async startNewAttempt() {
      try {
        this.setContext(null);
        await this.startNewLabAttempt();
        this.$router.push({ name: "Activity" });
      } catch (error) {
        alert(error.message);
      }
    },
    resumeAttempt(attemptNumber) {
      const attempt = this.getAttemptData(attemptNumber);
      if (!attempt || (attempt.isComplete && this.locksOnCompletion)) {
        return;
      }

      this.$router.push({ name: "Activity" });
    },
    downloadCSV(attemptNumber) {
      const attempt = this.getAttemptData(attemptNumber);
      if (!attempt) return;

      const csvData = this.generateCSV(attempt, attemptNumber);
      this.downloadFile(csvData, `lab_attempt_${attemptNumber}.csv`);
    },
    exportAllAttempts() {
      const csvData = this.generateAllAttemptsCSV();
      this.downloadFile(csvData, "all_lab_attempts.csv");
    },
    generateCSV(attempt, attemptNumber) {
      return exportLabAttemptCSV(attempt, attemptNumber, {
        formatDateTime: this.formatDateTime
      });
    },
    generateAllAttemptsCSV() {
      const rows = [];
      rows.push(["All Lab Attempts Summary"]);
      rows.push(["Attempt", "Status", "Started", "Measurements"]);
      for (let i = 1; i <= this.maxAttempts; i++) {
        const attempt = this.getAttemptData(i);
        if (attempt) {
          rows.push([
            `Attempt ${i}`,
            attempt.isComplete ? "Completed" : "In Progress",
            this.formatDateTime(attempt.timestamp),
            (attempt.measurements || []).length
          ]);
        } else {
          rows.push([`Attempt ${i}`, "Not Started", "", ""]);
        }
      }
      return rows.map((row) => row.join(",")).join("\n");
    },
    downloadFile(content, filename) {
      const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", filename);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    formatDateTime(dateString) {
      return new Date(dateString).toLocaleString();
    },
    confirmRestart(attemptNumber) {
      this.restartAttemptNumber = attemptNumber;
      this.showRestartModal = true;
    },
    cancelRestart() {
      this.showRestartModal = false;
      this.restartAttemptNumber = null;
    },
    async executeRestart() {
      if (!this.restartAttemptNumber) return;

      try {
        const attempt = this.getAttemptData(this.restartAttemptNumber);
        if (attempt) {
          this.removeLabAttempt(attempt.attemptId);
        }

        this.setContext(null);
        await this.startNewLabAttempt();

        this.showRestartModal = false;
        this.restartAttemptNumber = null;

        this.$router.push({ name: "Activity" });
      } catch (error) {
        this.showRestartModal = false;
        this.restartAttemptNumber = null;
        alert(`Error restarting: ${error.message}`);
      }
    }
  }
};
</script>
<style scoped>
.lab-attempt-card {
  border-left: 6px solid;
  border-radius: 6px;
}

/* Unique color per attempt */
.attempt-border-1 {
  border-left-color: #0d6efd;
} /* Blue */
.attempt-border-2 {
  border-left-color: #198754;
} /* Green */
.attempt-border-3 {
  border-left-color: #fd7e14;
} /* Orange */
.attempt-border-4 {
  border-left-color: #6f42c1;
} /* Purple */
.attempt-border-5 {
  border-left-color: #dc3545;
} /* Red */

/* Optional subtle hover polish */
.lab-attempt-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: box-shadow 0.2s ease;
}

.btn-outline-secondary {
  font-size: 0.875rem;
}

.btn-outline-secondary:hover {
  background-color: #6c757d;
  border-color: #6c757d;
  color: #fff;
}

.bi-chevron-up,
.bi-chevron-down {
  font-size: 0.75rem;
  margin-right: 0.25rem;
}
</style>
